import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, Tooltip } from "@mui/material";
import { atom, useAtom } from "jotai";
import {
  type ElementHint,
  type TreeNode,
} from "@/routes/sessions/DeviceContext";
import useMouse, { type MousePosition } from "@react-hook/mouse-position";
import { normalizeText } from "@/utils/helpers";
import {
  getHoveredElement,
  getMouseHint,
  toPercent,
} from "@/components/device-provider/utils";
import {
  CreateAnnotation,
  Crosshairs,
} from "@/routes/sessions/AnnotatedScreenshot";
import { type Dimensions } from "@/utils/types";

interface ZoomImageDialogState {
  open: boolean;
  imageSrc?: string;
  uiElements: TreeNode[];
}

const zoomImageDialogStateAtom = atom<ZoomImageDialogState>({
  open: false,
  uiElements: [],
});

const ZoomImageDialog = ({ deviceScreen }: { deviceScreen?: Dimensions }) => {
  const [zoomImageDialogState, setZoomImageDialogState] = useAtom(
    zoomImageDialogStateAtom
  );

  return (
    <Dialog
      open={zoomImageDialogState.open}
      maxWidth={"lg"}
      onClose={() => {
        setZoomImageDialogState({ open: false, uiElements: [] });
      }}
    >
      <DialogContent
        dividers
        sx={{
          position: "relative",
          height: "90vh",
          overflow: "hidden",
          padding: "0px",
        }}
      >
        <img
          src={zoomImageDialogState.imageSrc}
          style={{
            display: "block",
            height: "90vh",
          }}
        />
        {zoomImageDialogState.imageSrc &&
          zoomImageDialogState.uiElements &&
          zoomImageDialogState.uiElements.length > 0 &&
          deviceScreen && (
            <div
              style={{
                position: "absolute",
                height: "100%",
                width: "100%",
                top: 0,
                left: 0,
              }}
            >
              <AnnotatedScreenshot
                uiElements={zoomImageDialogState.uiElements}
                deviceScreen={deviceScreen}
              />
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
};

const AnnotatedScreenshot = ({
  uiElements,
  deviceScreen,
}: {
  uiElements: TreeNode[];
  deviceScreen: Dimensions;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hasMouseLeft, setHasMouseLeft] = useState<boolean>(false);
  const mouse = useMouse(ref, { enterDelay: 100, leaveDelay: 100 });
  const [hoveredElement, setHoveredElement] = useState<TreeNode | null>(null);
  const [footerHint, setFooterHint] = useState<ElementHint | null>(null);

  const getElementHint = useCallback(
    (element: TreeNode): ElementHint => {
      const text = element.text ?? element.attributes?.text;
      const resourceId =
        element.resourceId ?? element.attributes?.["resource-id"];
      const accessibilityText =
        element.accessibilityText ?? element.attributes?.["accessibility-text"];
      if (text ?? accessibilityText) {
        return {
          text: normalizeText(text ?? accessibilityText ?? ""),
          isResourceId: false,
        };
      }
      if (resourceId) {
        return { text: normalizeText(resourceId), isResourceId: true };
      }
      if (element.bounds == null) {
        return { text: "", isResourceId: false };
      }
      const cx = toPercent(
        element.bounds.x + element.bounds.width / 2,
        deviceScreen.width
      );
      const cy = toPercent(
        element.bounds.y + element.bounds.height / 2,
        deviceScreen.height
      );
      return { text: `${cx}, ${cy}`, isResourceId: false };
    },
    [deviceScreen]
  );

  const onHover = useCallback(
    (element: TreeNode | null, mouse: MousePosition | null) => {
      const mouseHint = mouse == null ? null : getMouseHint(mouse);
      const elementHint = element == null ? null : getElementHint(element);
      setFooterHint(
        elementHint ?? { text: mouseHint ?? "", isResourceId: false }
      );
      setHoveredElement(element);
    },
    [getElementHint]
  );

  useEffect(() => {
    if (deviceScreen == null) return;
    if (mouse.isOver) {
      setHasMouseLeft(false);

      const hoveredElement = getHoveredElement({
        dimensions: deviceScreen,
        mouse,
        usingGptDriverDeviceProvider: false,
        elements: uiElements,
      });

      onHover(hoveredElement, mouse);
    } else if (!hasMouseLeft) {
      setHasMouseLeft(true);
      onHover(null, null);
    }
  }, [mouse, hasMouseLeft, onHover, deviceScreen, uiElements]);

  const focusedElement = hoveredElement;

  const createCrosshairs = () => {
    if (!mouse.isOver) {
      const bounds = focusedElement?.bounds;
      if (bounds == null || deviceScreen == null) return null;
      const { x, y, width, height } = bounds;
      const cx = (x + width / 2) / deviceScreen.width;
      const cy = (y + height / 2) / deviceScreen.height;
      const color = "bg-pink-400";
      return <Crosshairs cx={cx} cy={cy} color={color} />;
    } else {
      if (mouse.x && mouse.y && mouse.elementWidth && mouse.elementHeight) {
        return (
          <Crosshairs
            cx={mouse.x / mouse.elementWidth}
            cy={mouse.y / mouse.elementHeight}
            color="bg-blue-400"
          />
        );
      } else {
        return null;
      }
    }
  };

  return (
    <Tooltip title={footerHint?.text} followCursor>
      <div
        ref={ref}
        className="relative overflow-hidden"
        style={{
          width: "100%",
          height: "100%",
          backgroundColor: "transparent",
        }}
        onClick={() => {}}
      >
        {createCrosshairs()}
        <div
          className="w-full h-full"
          style={{ backgroundColor: "transparent" }}
        >
          {uiElements.map((element, index) => (
            <CreateAnnotation
              key={index}
              element={element}
              inspectedElement={null}
              hoveredElement={hoveredElement}
              deviceScreen={deviceScreen}
              footerHint={footerHint}
              handleClick={() => {}}
            />
          ))}
        </div>
      </div>
    </Tooltip>
  );
};

export default ZoomImageDialog;
export { zoomImageDialogStateAtom };
